import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { useLang } from "@/i18n/LanguageContext";
import { MapPin, ExternalLink } from "lucide-react";

const IMG_HOUSE = "/about/house-main.jpg";
const IMG_DETAIL = "/about/secluded.jpg";

// Placeholder pin — generic "Sućuraj, Hvar". To use the exact villa coordinates,
// open Google Maps → Share → Embed a map and replace MAP_EMBED_SRC + MAP_LINK below.
const MAP_QUERY = "Sućuraj, Hvar, Croatia";
const MAP_EMBED_SRC = `https://maps.google.com/maps?q=${encodeURIComponent(
  MAP_QUERY
)}&t=&z=12&ie=UTF8&iwloc=&output=embed`;
const MAP_LINK = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
  MAP_QUERY
)}`;

// Scroll-driven cinematic zoom into the bottom-left corner of the image —
// the moment the villa "appears" out of the olive grove.
const ScrollZoomImage = ({ src, alt }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.7]);
  return (
    <div ref={ref} className="absolute inset-0 overflow-hidden">
      <motion.img
        src={src}
        alt={alt}
        loading="lazy"
        style={{ scale, transformOrigin: "0% 100%" }}
        className="absolute inset-0 w-full h-full object-cover will-change-transform"
      />
    </div>
  );
};

export const About = () => {
  const { t } = useLang();

  return (
    <section
      id="about"
      data-testid="about-section"
      className="relative bg-[#F8F5F0] py-24 md:py-32"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-start">
          {/* Left: copy */}
          <div className="lg:col-span-6">
            <span className="text-xs uppercase tracking-[0.28em] text-[#8A9A86] font-semibold">
              {t.about.eyebrow}
            </span>
            <h2
              data-testid="about-title"
              className="mt-6 font-serif font-light text-[#2C302E] text-4xl md:text-5xl leading-[1.08] tracking-tight"
            >
              {t.about.title}
            </h2>
            <div className="mt-10 space-y-6 text-[#5C625E] text-base md:text-[1.05rem] leading-relaxed font-light max-w-xl">
              <p>{t.about.paragraph1}</p>
              <p>{t.about.paragraph2}</p>
            </div>

            {/* Location card */}
            <div className="mt-12 pl-6 border-l-2 border-[#C05C3D]">
              <div className="flex items-center gap-2 text-xs uppercase tracking-[0.24em] text-[#1D4B69] font-semibold">
                <MapPin className="h-3.5 w-3.5" />
                {t.about.locationTitle}
              </div>
              <p className="mt-4 text-[#2C302E] text-base leading-relaxed font-light max-w-lg">
                {t.about.locationText}
              </p>
            </div>
          </div>

          {/* Right: image stack + features */}
          <div className="lg:col-span-6">
            <div className="grid grid-cols-12 gap-4">
              <div className="col-span-12 sm:col-span-8 aspect-[4/5] overflow-hidden">
                <img
                  src={IMG_HOUSE}
                  alt="Villa Sućuraj — stone exterior"
                  className="w-full h-full object-cover hover:scale-105 transition-transform duration-1000 ease-out"
                  data-testid="about-image-main"
                />
              </div>
              <div className="col-span-12 sm:col-span-4 flex flex-col gap-4">
                <div
                  className="relative aspect-square overflow-hidden"
                  data-testid="about-image-secluded"
                >
                  <ScrollZoomImage
                    src={IMG_DETAIL}
                    alt="Aerial view — Villa Sućuraj nestled in its private olive grove"
                  />
                </div>
                <div className="flex-1 bg-[#EAE4D9] p-6 flex items-end">
                  <span className="text-xs uppercase tracking-[0.24em] text-[#2C302E] font-semibold leading-relaxed">
                    Eastern Hvar<br />
                    43°07′N · 17°11′E
                  </span>
                </div>
              </div>
            </div>

            {/* Features */}
            <div className="mt-12">
              <div className="text-xs uppercase tracking-[0.28em] text-[#8A9A86] font-semibold">
                {t.about.featuresTitle}
              </div>
              <ul
                data-testid="about-features"
                className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4"
              >
                {t.about.features.map((f, i) => (
                  <li
                    key={i}
                    className="flex items-start gap-3 text-[#2C302E] text-sm leading-relaxed font-light border-b border-[#2C302E]/10 pb-3"
                  >
                    <span className="text-[#C05C3D] font-serif text-base leading-none mt-0.5">
                      0{i + 1}
                    </span>
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        {/* Google Map */}
        <div className="mt-20 md:mt-28">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
            <div>
              <span className="text-xs uppercase tracking-[0.28em] text-[#8A9A86] font-semibold">
                {t.about.locationTitle}
              </span>
              <h3
                data-testid="about-map-title"
                className="mt-3 font-serif font-light text-[#2C302E] text-3xl md:text-4xl leading-tight tracking-tight"
              >
                {t.about.mapTitle}
              </h3>
            </div>
            <a
              href={MAP_LINK}
              target="_blank"
              rel="noreferrer"
              data-testid="about-map-link"
              className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] font-semibold text-[#C05C3D] hover:text-[#a64c30] transition-colors"
            >
              {t.about.mapDirections}
              <ExternalLink className="h-3.5 w-3.5" />
            </a>
          </div>
          <div
            data-testid="about-map-embed"
            className="relative aspect-[16/9] md:aspect-[21/9] overflow-hidden border border-[#2C302E]/10 bg-[#EAE4D9]"
          >
            <iframe
              title="Villa Sućuraj — Google Maps"
              src={MAP_EMBED_SRC}
              className="absolute inset-0 w-full h-full"
              style={{ border: 0, filter: "grayscale(20%) contrast(0.95)" }}
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              allowFullScreen
            />
          </div>
        </div>
      </div>
    </section>
  );
};
